import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  InputGroup,
  Badge,
} from "react-bootstrap";
import {
  IconPalette,
  IconCheck,
  IconUpload,
  IconEye,
  IconColorPicker,
  IconArrowLeft,
  IconRefresh,
  IconX,
  IconShare,
  IconMail,
  IconShieldLock,
  IconPlugConnected,
  IconAlertTriangle,
  IconLayoutSidebar,
} from "@tabler/icons-react";
import { HexColorPicker } from 'react-colorful';
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import OrganizationService from "./services/OrganizationServices";
import UserContext from "../../contexts/UserContext";

// Gradientes pré-definidos
const PRESET_GRADIENTS = [
  {
    name: "Primary",
    value: "linear-gradient(135deg, #305CDE 0%, #1ea7c5 100%)",
    color1: "#305CDE",
    color2: "#1ea7c5"
  },
  {
    name: "Success",
    value: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    color1: "#10b981",
    color2: "#059669"
  },
  {
    name: "Danger",
    value: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
    color1: "#ef4444",
    color2: "#dc2626"
  },
  {
    name: "Warning",
    value: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    color1: "#f59e0b",
    color2: "#d97706"
  },
  {
    name: "Info",
    value: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
    color1: "#06b6d4",
    color2: "#0891b2"
  },
  {
    name: "Neutral",
    value: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
    color1: "#f8fafc",
    color2: "#e2e8f0"
  },
  {
    name: "Highlight",
    value: "linear-gradient(135deg, #305cde 0%, #1ea7c5 100%)",
    color1: "#305cde",
    color2: "#1ea7c5"
  },
  {
    name: "Pink",
    value: "linear-gradient(135deg, #d64c9d 0%, #e83e8c 100%)",
    color1: "#d64c9d",
    color2: "#e83e8c"
  }
];

// ============================================================
// Utilitários de contraste (WCAG) — guarda-corpo de legibilidade
// ============================================================
const _hexToRgb = (hex) => {
  if (!hex || typeof hex !== "string") return null;
  let h = hex.replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};
const _lum = (rgb) => {
  const a = [rgb.r, rgb.g, rgb.b].map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
};
const contrastRatio = (a, b) => {
  const ra = _hexToRgb(a);
  const rb = _hexToRgb(b);
  if (!ra || !rb) return 21;
  const la = _lum(ra);
  const lb = _lum(rb);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};
const readableOn = (bg) => {
  const rgb = _hexToRgb(bg);
  if (!rgb) return "#ffffff";
  return _lum(rgb) > 0.5 ? "#0f172a" : "#ffffff";
};

// Presets de tema do menu lateral (sidebar)
const SIDEBAR_PRESETS = {
  light: { label: "Claro", bg: "#ffffff", text: "#475569", icon: "#64748b", active_text: "#ffffff" },
  dark: { label: "Escuro", bg: "#111827", text: "#e5e7eb", icon: "#9ca3af", active_text: "#ffffff" },
  brand: { label: "Cor da marca" },
  custom: { label: "Personalizado" },
};


const OrganizationCustomization = () => {
  const navigate = useNavigate();
  const { getOrganizationInfo } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [organizationData, setOrganizationData] = useState(null);
  const [formData, setFormData] = useState({
    primaryColor: "#305CDE",
    secondaryColor: "#1ea7c5",
    textColor: "#000000",
    gradientColor1: "#305CDE",
    gradientColor2: "#1ea7c5",
    selectedGradient: "",
    logo: null,
    logoMobile: null,
    favicon: null,
    socialBanner: null,
    customDomain: "",
    siteTitle: "",
    siteDescription: "",
    logoWidth: "210",
    // Menu lateral (sidebar)
    sidebarStyle: "light",
    sidebarBg: "#ffffff",
    sidebarText: "#475569",
    sidebarIcon: "#64748b",
    sidebarActiveBg: "",
    sidebarActiveText: "#ffffff",
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoMobilePreview, setLogoMobilePreview] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);
  const [socialBannerPreview, setSocialBannerPreview] = useState(null);
  const [showColorPickers, setShowColorPickers] = useState({
    primaryColor: false,
    secondaryColor: false,
    textColor: false
  });
  const [showLogoSizePreview, setShowLogoSizePreview] = useState(false);

  // SMTP white label
  const SMTP_PASSWORD_MASK = "••••••••";
  const [smtpData, setSmtpData] = useState({
    smtp_host: "",
    smtp_port: "",
    smtp_username: "",
    smtp_password: "",
    smtp_use_tls: true,
    smtp_use_ssl: false,
    smtp_from_email: "",
    smtp_from_name: "",
  });
  const [smtpServerState, setSmtpServerState] = useState({
    smtp_password_set: false,
    has_smtp_configured: false,
    smtp_last_error: null,
    smtp_failure_count: 0,
    smtp_disabled_until: null,
  });
  const [smtpDirty, setSmtpDirty] = useState(false);
  const [smtpTestPassed, setSmtpTestPassed] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpTestRecipient, setSmtpTestRecipient] = useState("");
  const [smtpSaving, setSmtpSaving] = useState(false);
  // Status do último teste para mostrar no badge persistente
  // null = nunca testado nesta sessão; 'passed' | 'failed'
  const [smtpLastTestStatus, setSmtpLastTestStatus] = useState(null);
  const [smtpLastTestError, setSmtpLastTestError] = useState(null);

  useEffect(() => {
    const loadOrganizationData = async () => {
      try {
        const orgInfo = getOrganizationInfo();
        if (orgInfo?.id) {
          const data = await OrganizationService.getOrganization(orgInfo.id);
          setOrganizationData(data);
          
          setFormData({
            primaryColor: data.primary_color || "#305CDE",
            secondaryColor: data.titles_color || "#1ea7c5",
            textColor: data.text_color || "#000000",
            gradientColor1: data.gradient_color1 || "#305CDE",
            gradientColor2: data.gradient_color2 || "#1ea7c5",
            selectedGradient: data.selected_gradient || "",
            logo: null,
            logoMobile: null,
            favicon: null,
            customDomain: data.custom_domain || "",
            siteTitle: data.site_title || "",
            siteDescription: data.site_description || "",
            logoWidth: (data.css_customizations?.logo_width && data.css_customizations.logo_width.replace('px', '')) || "150",
            sidebarStyle: data.sidebar_style || "light",
            sidebarBg: data.sidebar_bg || "#ffffff",
            sidebarText: data.sidebar_text || "#475569",
            sidebarIcon: data.sidebar_icon || "#64748b",
            sidebarActiveBg: data.sidebar_active_bg || "",
            sidebarActiveText: data.sidebar_active_text || "#ffffff",
          });
          
          // Definir previews de imagens existentes
          if (data.logo) setLogoPreview(data.logo);
          if (data.logo_mobile) setLogoMobilePreview(data.logo_mobile);
          if (data.favicon) setFaviconPreview(data.favicon);
          if (data.social_banner) setSocialBannerPreview(data.social_banner);

          // Carregar configuração SMTP da organização (senha mascarada)
          try {
            const smtp = await OrganizationService.getOrganizationSMTP(orgInfo.id);
            setSmtpData({
              smtp_host: smtp.smtp_host || "",
              smtp_port: smtp.smtp_port || "",
              smtp_username: smtp.smtp_username || "",
              smtp_password: smtp.smtp_password_set ? SMTP_PASSWORD_MASK : "",
              smtp_use_tls: smtp.smtp_use_tls !== undefined ? smtp.smtp_use_tls : true,
              smtp_use_ssl: smtp.smtp_use_ssl || false,
              smtp_from_email: smtp.smtp_from_email || "",
              smtp_from_name: smtp.smtp_from_name || "",
            });
            setSmtpServerState({
              smtp_password_set: smtp.smtp_password_set || false,
              has_smtp_configured: smtp.has_smtp_configured || false,
              smtp_last_error: smtp.smtp_last_error || null,
              smtp_failure_count: smtp.smtp_failure_count || 0,
              smtp_disabled_until: smtp.smtp_disabled_until || null,
            });
            // Se já está configurada e funcionando, considera "teste passado"
            // para não bloquear save de mudanças não-SMTP
            setSmtpTestPassed(smtp.has_smtp_configured || false);
            setSmtpDirty(false);
          } catch (smtpError) {
            console.warn("Não foi possível carregar config SMTP:", smtpError);
          }
        }
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
      }
    };

    loadOrganizationData();
  }, [getOrganizationInfo]);

  const handleSmtpFieldChange = (field, value) => {
    setSmtpData((prev) => ({ ...prev, [field]: value }));
    setSmtpDirty(true);
    setSmtpTestPassed(false);
    setSmtpLastTestStatus(null);
    setSmtpLastTestError(null);
  };

  const buildSmtpPayload = () => {
    const payload = {
      smtp_host: smtpData.smtp_host || null,
      smtp_port: smtpData.smtp_port ? parseInt(smtpData.smtp_port, 10) : null,
      smtp_username: smtpData.smtp_username || null,
      smtp_use_tls: !!smtpData.smtp_use_tls,
      smtp_use_ssl: !!smtpData.smtp_use_ssl,
      smtp_from_email: smtpData.smtp_from_email || null,
      smtp_from_name: smtpData.smtp_from_name || null,
    };
    // Senha: só enviar se foi alterada (diferente do mask)
    if (smtpData.smtp_password && smtpData.smtp_password !== SMTP_PASSWORD_MASK) {
      payload.smtp_password = smtpData.smtp_password;
    } else if (smtpData.smtp_password === "" && smtpServerState.smtp_password_set && smtpDirty) {
      // Usuário limpou a senha intencionalmente
      payload.smtp_password = "";
    }
    return payload;
  };

  const handleTestSmtp = async () => {
    const orgInfo = getOrganizationInfo();
    if (!orgInfo?.id) return;
    const recipient = (smtpTestRecipient || "").trim();
    if (!recipient || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient)) {
      Swal.fire({
        icon: "warning",
        title: "Email de teste inválido",
        text: "Informe um email válido para receber o teste de SMTP.",
      });
      return;
    }
    setSmtpTesting(true);
    try {
      const payload = { ...buildSmtpPayload(), test_recipient: recipient };
      const result = await OrganizationService.testSMTP(orgInfo.id, payload);
      if (result?.success) {
        setSmtpTestPassed(true);
        setSmtpLastTestStatus("passed");
        setSmtpLastTestError(null);
        Swal.fire({
          icon: "success",
          title: "Teste SMTP bem-sucedido",
          text: `Email de teste enviado para ${recipient}.`,
          timer: 2200,
          showConfirmButton: false,
        });
      } else {
        setSmtpTestPassed(false);
        const errMsg = result?.error || result?.smtp_error || "Não foi possível conectar.";
        setSmtpLastTestStatus("failed");
        setSmtpLastTestError(errMsg);
        Swal.fire({
          icon: "error",
          title: "Teste SMTP falhou",
          text: errMsg,
        });
      }
    } catch (error) {
      setSmtpTestPassed(false);
      const errMsg = error?.response?.data?.error || error?.message || String(error);
      setSmtpLastTestStatus("failed");
      setSmtpLastTestError(errMsg);
      Swal.fire({
        icon: "error",
        title: "Erro ao testar SMTP",
        text: errMsg,
      });
    } finally {
      setSmtpTesting(false);
    }
  };

  const handleSaveSmtp = async () => {
    const orgInfo = getOrganizationInfo();
    if (!orgInfo?.id) return;
    setSmtpSaving(true);
    try {
      const payload = buildSmtpPayload();
      if (smtpTestRecipient) {
        payload.test_recipient = smtpTestRecipient;
      }
      const result = await OrganizationService.updateOrganizationSMTP(orgInfo.id, payload);
      Swal.fire({
        icon: "success",
        title: "Configuração SMTP salva",
        text: result?.message || "Configuração testada e salva.",
        timer: 2500,
        showConfirmButton: false,
      });
      setSmtpDirty(false);
      setSmtpTestPassed(true);
      setSmtpLastTestStatus("passed");
      setSmtpLastTestError(null);
      setSmtpServerState({
        smtp_password_set: result?.smtp_password_set || false,
        has_smtp_configured: result?.has_smtp_configured || false,
        smtp_last_error: result?.smtp_last_error || null,
        smtp_failure_count: result?.smtp_failure_count || 0,
        smtp_disabled_until: result?.smtp_disabled_until || null,
      });
      if (result?.smtp_password_set && !smtpData.smtp_password) {
        setSmtpData((prev) => ({ ...prev, smtp_password: SMTP_PASSWORD_MASK }));
      }
    } catch (error) {
      const errData = error?.response?.data || {};
      Swal.fire({
        icon: "error",
        title: "Não foi possível salvar SMTP",
        text: errData.error || errData.smtp_error || error?.message || "Erro ao salvar.",
      });
    } finally {
      setSmtpSaving(false);
    }
  };

  const handleClearSmtp = async () => {
    const orgInfo = getOrganizationInfo();
    if (!orgInfo?.id) return;
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Remover SMTP custom?",
      text: "Os emails voltarão a ser enviados pelo servidor padrão do Mercado Agentes.",
      showCancelButton: true,
      confirmButtonText: "Remover",
      cancelButtonText: "Cancelar",
    });
    if (!confirm.isConfirmed) return;

    setSmtpSaving(true);
    try {
      await OrganizationService.updateOrganizationSMTP(orgInfo.id, {
        smtp_host: null,
        smtp_port: null,
        smtp_username: null,
        smtp_password: "",
        smtp_from_email: null,
        smtp_from_name: null,
      });
      setSmtpData({
        smtp_host: "",
        smtp_port: "",
        smtp_username: "",
        smtp_password: "",
        smtp_use_tls: true,
        smtp_use_ssl: false,
        smtp_from_email: "",
        smtp_from_name: "",
      });
      setSmtpServerState({
        smtp_password_set: false,
        has_smtp_configured: false,
        smtp_last_error: null,
        smtp_failure_count: 0,
        smtp_disabled_until: null,
      });
      setSmtpDirty(false);
      setSmtpTestPassed(true);
      setSmtpLastTestStatus(null);
      setSmtpLastTestError(null);
      Swal.fire({ icon: "success", title: "SMTP removido", timer: 2000, showConfirmButton: false });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Erro ao remover SMTP",
        text: error?.response?.data?.error || error?.message || "Erro.",
      });
    } finally {
      setSmtpSaving(false);
    }
  };

  const smtpDomainMismatch = (() => {
    if (!smtpData.smtp_username || !smtpData.smtp_from_email) return false;
    const u = smtpData.smtp_username.split("@")[1] || "";
    const f = smtpData.smtp_from_email.split("@")[1] || "";
    return u && f && u.toLowerCase() !== f.toLowerCase();
  })();

  const smtpDisabledNow = smtpServerState.smtp_disabled_until && new Date(smtpServerState.smtp_disabled_until) > new Date();

  const handleColorChange = (colorField, value) => {
    setFormData((prev) => ({
      ...prev,
      [colorField]: value,
    }));
  };

  // Aplica um preset de sidebar, derivando cores legíveis quando necessário
  const handleSidebarPreset = (style) => {
    setFormData((prev) => {
      if (style === "light" || style === "dark") {
        const p = SIDEBAR_PRESETS[style];
        return {
          ...prev,
          sidebarStyle: style,
          sidebarBg: p.bg,
          sidebarText: p.text,
          sidebarIcon: p.icon,
          sidebarActiveBg: "",
          sidebarActiveText: p.active_text,
        };
      }
      if (style === "brand") {
        const bg = prev.primaryColor || "#305CDE";
        const fg = readableOn(bg);
        // Menu com a cor da marca; item ativo vira uma "pílula" branca com texto da marca
        return {
          ...prev,
          sidebarStyle: "brand",
          sidebarBg: bg,
          sidebarText: fg,
          sidebarIcon: fg,
          sidebarActiveBg: "#ffffff",
          sidebarActiveText: bg,
        };
      }
      return { ...prev, sidebarStyle: "custom" };
    });
  };

  // Alteração manual de uma cor do sidebar -> marca como "personalizado"
  const handleSidebarColorChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value, sidebarStyle: "custom" }));
  };

  const handleFileUpload = (fileType, event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar arquivo antes de processar
      const validation = validateFile(file, fileType);
      if (!validation.isValid) {
        Swal.fire({
          icon: "error",
          title: "Arquivo Inválido",
          text: validation.errors.join(", "),
        });
        return;
      }

      if (fileType === "logo") {
        setFormData((prev) => ({ ...prev, logo: file }));
        const reader = new FileReader();
        reader.onload = (e) => setLogoPreview(e.target.result);
        reader.readAsDataURL(file);
      } else if (fileType === "logoMobile") {
        setFormData((prev) => ({ ...prev, logoMobile: file }));
        const reader = new FileReader();
        reader.onload = (e) => setLogoMobilePreview(e.target.result);
        reader.readAsDataURL(file);
      } else if (fileType === "favicon") {
        setFormData((prev) => ({ ...prev, favicon: file }));
        const reader = new FileReader();
        reader.onload = (e) => setFaviconPreview(e.target.result);
        reader.readAsDataURL(file);
      } else if (fileType === "socialBanner") {
        setFormData((prev) => ({ ...prev, socialBanner: file }));
        const reader = new FileReader();
        reader.onload = (e) => setSocialBannerPreview(e.target.result);
        reader.readAsDataURL(file);
      }
    }
  };

  const validateFile = (file, type) => {
    const errors = [];
    const allowedTypes = {
      logo: ["image/jpeg", "image/png", "image/svg+xml"],
      logoMobile: ["image/jpeg", "image/png", "image/svg+xml"],
      favicon: ["image/x-icon", "image/png"],
      socialBanner: ["image/jpeg", "image/png"],
    };
    const maxSizes = {
      logo: 2 * 1024 * 1024, // 2MB
      logoMobile: 2 * 1024 * 1024, // 2MB
      favicon: 100 * 1024, // 100KB
      socialBanner: 1 * 1024 * 1024, // 1MB
    };

    if (!allowedTypes[type].includes(file.type)) {
      errors.push(`Formato não suportado para ${type}`);
    }

    if (file.size > maxSizes[type]) {
      const maxSizeText = type === "favicon" ? "100KB" : type === "socialBanner" ? "1MB" : "2MB";
      errors.push(`Arquivo muito grande. Máximo: ${maxSizeText}`);
    }

    return { isValid: errors.length === 0, errors };
  };

  const resetColors = () => {
    setFormData((prev) => ({
      ...prev,
      primaryColor: "#305CDE",
      secondaryColor: "#1ea7c5",
      textColor: "#000000",
      gradientColor1: "#305CDE",
      gradientColor2: "#1ea7c5",
      selectedGradient: "",
    }));
  };

  // Função para gerar gradiente baseado nas cores selecionadas
  const generateGradient = (color1, color2) => {
    return `linear-gradient(135deg, ${color1} 0%, ${color2} 100%)`;
  };

  // Função para lidar com seleção de gradiente pré-definido
  const handlePresetGradientSelect = (gradient) => {
    setFormData((prev) => ({
      ...prev,
      gradientColor1: gradient.color1,
      gradientColor2: gradient.color2,
      selectedGradient: gradient.name,
    }));
  };

  const GradientPicker = ({ label, color1, color2, selectedPreset, onChange, onPresetSelect, helpText }) => {
    const currentGradient = generateGradient(color1, color2);

    return (
      <Col md={12} className="mb-3">
        <Form.Label className="fw-semibold d-flex align-items-center">
          <IconPalette size={16} className="me-2" />
          {label}
        </Form.Label>
        
        {/* Gradientes Pré-definidos */}
        <div className="mb-3">
          <small className="text-muted mb-2 d-block">Gradientes Pré-definidos:</small>
          <div className="d-flex flex-wrap gap-2">
            {PRESET_GRADIENTS.map((gradient) => (
              <div
                key={gradient.name}
                className={`gradient-preset ${selectedPreset === gradient.name ? 'selected' : ''}`}
                onClick={() => onPresetSelect(gradient)}
                style={{
                  width: "40px",
                  height: "40px",
                  background: gradient.value,
                  borderRadius: "8px",
                  cursor: "pointer",
                  border: selectedPreset === gradient.name ? "3px solid #305CDE" : "2px solid #dee2e6",
                  boxShadow: selectedPreset === gradient.name ? "0 0 0 2px rgba(48, 92, 222, 0.2)" : "none",
                  transition: "all 0.2s ease"
                }}
                title={gradient.name}
              />
            ))}
          </div>
        </div>

        {/* Seletores de Cor Personalizados */}
        <div className="mb-3">
          <small className="text-muted mb-2 d-block">Cores Personalizadas:</small>
          <Row>
            <Col md={6}>
              <Form.Label className="small">Cor 1:</Form.Label>
              <div className="d-flex align-items-center gap-2">
                <Form.Control
                  type="color"
                  value={color1}
                  onChange={(e) => onChange('gradientColor1', e.target.value)}
                  style={{ 
                    width: "40px", 
                    height: "32px", 
                    padding: "2px",
                    cursor: "pointer",
                    border: "2px solid #dee2e6"
                  }}
                />
                <Form.Control
                  type="text"
                  value={color1}
                  onChange={(e) => onChange('gradientColor1', e.target.value)}
                  placeholder="#000000"
                  style={{ fontFamily: "monospace", fontSize: "0.875rem" }}
                />
              </div>
            </Col>
            <Col md={6}>
              <Form.Label className="small">Cor 2:</Form.Label>
              <div className="d-flex align-items-center gap-2">
                <Form.Control
                  type="color"
                  value={color2}
                  onChange={(e) => onChange('gradientColor2', e.target.value)}
                  style={{ 
                    width: "40px", 
                    height: "32px", 
                    padding: "2px",
                    cursor: "pointer",
                    border: "2px solid #dee2e6"
                  }}
                />
                <Form.Control
                  type="text"
                  value={color2}
                  onChange={(e) => onChange('gradientColor2', e.target.value)}
                  placeholder="#000000"
                  style={{ fontFamily: "monospace", fontSize: "0.875rem" }}
                />
              </div>
            </Col>
          </Row>
        </div>

        {/* Preview do Gradiente */}
        <div className="mb-2">
          <small className="text-muted mb-1 d-block">Preview:</small>
          <div
            className="rounded"
            style={{
              width: "100%",
              height: "40px",
              background: currentGradient,
              border: "1px solid #dee2e6",
              boxShadow: "inset 0 1px 2px rgba(0,0,0,0.1)"
            }}
          />
        </div>

        {helpText && (
          <Form.Text className="text-muted small">{helpText}</Form.Text>
        )}
      </Col>
    );
  };

  const ColorPicker = ({ label, value, onChange, helpText, colorKey }) => {
    const isOpen = showColorPickers[colorKey];
    const [tempColor, setTempColor] = useState(value);
    
    const togglePicker = () => {
      if (isOpen) {
        // Confirmar cor selecionada
        onChange(tempColor);
      } else {
        // Abrir picker e definir cor temporária
        setTempColor(value);
      }
      
      setShowColorPickers(prev => ({
        ...prev,
        [colorKey]: !prev[colorKey]
      }));
    };

    const cancelSelection = () => {
      setTempColor(value); // Restaurar cor original
      setShowColorPickers(prev => ({
        ...prev,
        [colorKey]: false
      }));
    };

    return (
      <Col md={6} className="mb-3">
        <Form.Label className="fw-semibold d-flex align-items-center">
          <IconColorPicker size={16} className="me-2" />
          {label}
        </Form.Label>
        <div className="d-flex align-items-center gap-2 mb-2">
          <div
            onClick={togglePicker}
            style={{ 
              width: "50px", 
              height: "38px", 
              backgroundColor: value,
              cursor: "pointer",
              border: "2px solid #dee2e6",
              borderRadius: "4px"
            }}
          />
          <Form.Control
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="#000000"
            pattern="^#[0-9A-Fa-f]{6}$"
            style={{ fontFamily: "monospace" }}
          />
        </div>
        
        {isOpen && (
          <div className="p-3 border rounded bg-light position-relative" style={{ zIndex: 1000 }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="fw-semibold">Selecionar Cor:</small>
              <div className="d-flex gap-1">
                <Button 
                  variant="outline-secondary" 
                  size="sm"
                  onClick={cancelSelection}
                  title="Cancelar"
                >
                  <IconX size={12} />
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={togglePicker}
                  title="Confirmar"
                >
                  <IconCheck size={12} />
                </Button>
              </div>
            </div>
            <HexColorPicker 
              color={tempColor} 
              onChange={setTempColor}
            />
            <div className="mt-2 d-flex align-items-center gap-2">
              <div
                style={{
                  width: "30px",
                  height: "30px",
                  backgroundColor: tempColor,
                  border: "1px solid #dee2e6",
                  borderRadius: "4px"
                }}
              />
              <span style={{ fontFamily: "monospace", fontSize: "0.875rem" }}>
                {tempColor}
              </span>
            </div>
          </div>
        )}
        
        {helpText && (
          <Form.Text className="text-muted small">{helpText}</Form.Text>
        )}
        <div
          className="mt-2 rounded"
          style={{
            width: "100%",
            height: "20px",
            backgroundColor: value,
            border: "1px solid #dee2e6",
          }}
        />
      </Col>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Se SMTP foi alterado, não permite salvar branding sem antes salvar/testar SMTP
    if (smtpDirty) {
      Swal.fire({
        icon: "warning",
        title: "Configuração SMTP pendente",
        text: "Você alterou campos do SMTP. Clique em 'Testar e Salvar SMTP' antes de salvar o restante das configurações.",
      });
      return;
    }

    setLoading(true);

    try {
      const gradientPrimary = `linear-gradient(135deg, ${formData.gradientColor1} 0%, ${formData.gradientColor2} 100%)`;
      
      const dataToSend = {
        primary_color: formData.primaryColor,
        titles_color: formData.secondaryColor,
        text_color: formData.textColor,
        gradient_primary: gradientPrimary,
        gradient_color1: formData.gradientColor1,
        gradient_color2: formData.gradientColor2,
        selected_gradient: formData.selectedGradient,
        custom_domain: formData.customDomain,
        site_title: formData.siteTitle,
        site_description: formData.siteDescription,
        css_customizations: JSON.stringify({
          logo_width: `${formData.logoWidth}px`,
        }),
        // Menu lateral (sidebar)
        sidebar_style: formData.sidebarStyle,
        sidebar_bg: formData.sidebarBg,
        sidebar_text: formData.sidebarText,
        sidebar_icon: formData.sidebarIcon,
        sidebar_active_bg: formData.sidebarActiveBg || "",
        sidebar_active_text: formData.sidebarActiveText,
      };

      // Adicionar apenas arquivos que foram realmente alterados pelo usuário
      if (formData.logo instanceof File) {
        dataToSend.logo = formData.logo;
      }
      if (formData.logoMobile instanceof File) {
        dataToSend.logo_mobile = formData.logoMobile;
      }
      if (formData.favicon instanceof File) {
        dataToSend.favicon = formData.favicon;
      }
      if (formData.socialBanner instanceof File) {
        dataToSend.social_banner = formData.socialBanner;
      }

      // Detectar se já existe branding (baseado na presença de cores ou logos existentes)
      const hasExistingBranding = organizationData && (
        organizationData.primary_color || 
        organizationData.logo || 
        organizationData.logo_mobile || 
        organizationData.favicon ||
        organizationData.custom_domain
      );

      const response = await OrganizationService.updateBranding(organizationData.id, dataToSend, hasExistingBranding);
      
      // Atualizar cache do WhitelabelContext com os novos dados
      const cacheUpdated = OrganizationService.updateWhitelabelCache(response);
      
      if (cacheUpdated) {
        // Disparar evento customizado para o WhitelabelContext
        window.dispatchEvent(new CustomEvent('whitelabel-cache-updated', {
          detail: { source: 'organization-customization', cacheKey: 'whitelabel_config' }
        }));
      }

      Swal.fire({
        icon: "success",
        title: "Personalização Salva!",
        text: "As configurações foram aplicadas com sucesso. A página será recarregada para aplicar as mudanças.",
        confirmButtonColor: formData.primaryColor,
        showConfirmButton: false,
        timer: 2500
      });
      
      // Recarregar a página para aplicar as mudanças visualmente
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (error) {
      console.error("Erro ao salvar:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível salvar as configurações.",
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center mb-3">
            <Button
              variant="outline-secondary"
              onClick={() => navigate(-1)}
              className="me-3"
            >
              <IconArrowLeft size={16} />
            </Button>
            <h1 className="h3 mb-0 d-flex align-items-center">
              <IconPalette size={28} className="me-2 text-primary" />
              Personalização da Organização
            </h1>
          </div>
        </Col>
      </Row>

      <Form onSubmit={handleSubmit}>
        <Row>
          <Col lg={8}>
            {/* Seção de Cores */}
            <Card className="mb-4 border-0 shadow-sm">
              <Card.Header className="bg-light border-0">
                <h6 className="mb-0 fw-bold d-flex align-items-center">
                  <IconPalette size={18} className="me-2" />
                  Cores da Marca
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="ms-auto"
                    onClick={resetColors}
                  >
                    <IconRefresh size={14} className="me-1" />
                    Resetar
                  </Button>
                </h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  <ColorPicker
                    label="Cor Primária"
                    value={formData.primaryColor}
                    onChange={(value) => handleColorChange("primaryColor", value)}
                    helpText="Cor principal da interface"
                    colorKey="primaryColor"
                  />
                  <ColorPicker
                    label="Cor de Títulos"
                    value={formData.secondaryColor}
                    onChange={(value) =>
                      handleColorChange("secondaryColor", value)
                    }
                    helpText="Cor dos títulos e textos de destaque"
                    colorKey="secondaryColor"
                  />
                </Row>

                <Row>
                  <ColorPicker
                    label="Cor do Texto"
                    value={formData.textColor}
                    onChange={(value) => handleColorChange("textColor", value)}
                    helpText="Cor padrão do texto"
                    colorKey="textColor"
                  />
                </Row>

                <Row>
                  <GradientPicker
                    label="Gradiente Primário"
                    color1={formData.gradientColor1}
                    color2={formData.gradientColor2}
                    selectedPreset={formData.selectedGradient}
                    onChange={handleColorChange}
                    onPresetSelect={handlePresetGradientSelect}
                    helpText="Gradiente usado em botões e elementos de destaque"
                  />
                </Row>
              </Card.Body>
            </Card>

            {/* Seção Menu Lateral (Sidebar) */}
            <Card className="mb-4 border-0 shadow-sm">
              <Card.Header className="bg-light border-0">
                <h6 className="mb-0 fw-bold d-flex align-items-center">
                  <IconLayoutSidebar size={18} className="me-2" />
                  Menu Lateral (Sidebar)
                </h6>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <small className="text-muted mb-2 d-block">Tema do menu:</small>
                  <div className="d-flex flex-wrap gap-2">
                    {Object.entries(SIDEBAR_PRESETS).map(([key, p]) => (
                      <Button
                        key={key}
                        size="sm"
                        variant={
                          formData.sidebarStyle === key
                            ? "primary"
                            : "outline-secondary"
                        }
                        style={
                          formData.sidebarStyle === key
                            ? {
                                backgroundColor: formData.primaryColor,
                                borderColor: formData.primaryColor,
                              }
                            : undefined
                        }
                        onClick={() => handleSidebarPreset(key)}
                      >
                        {p.label}
                      </Button>
                    ))}
                  </div>
                </div>

                <Row>
                  {[
                    { field: "sidebarBg", label: "Fundo do menu" },
                    { field: "sidebarText", label: "Texto do menu" },
                    { field: "sidebarIcon", label: "Ícones" },
                    { field: "sidebarActiveText", label: "Texto do item ativo" },
                  ].map(({ field, label }) => (
                    <Col md={6} className="mb-3" key={field}>
                      <Form.Label className="small fw-semibold">{label}</Form.Label>
                      <div className="d-flex align-items-center gap-2">
                        <Form.Control
                          type="color"
                          value={formData[field] || "#ffffff"}
                          onChange={(e) =>
                            handleSidebarColorChange(field, e.target.value)
                          }
                          style={{ width: "44px", height: "34px", padding: "2px" }}
                        />
                        <Form.Control
                          type="text"
                          value={formData[field]}
                          onChange={(e) =>
                            handleSidebarColorChange(field, e.target.value)
                          }
                          placeholder="#000000"
                          style={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                        />
                      </div>
                    </Col>
                  ))}
                  <Col md={6} className="mb-3">
                    <Form.Label className="small fw-semibold">
                      Fundo do item ativo
                    </Form.Label>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Control
                        type="color"
                        value={
                          formData.sidebarActiveBg ||
                          formData.primaryColor ||
                          "#305CDE"
                        }
                        onChange={(e) =>
                          handleSidebarColorChange("sidebarActiveBg", e.target.value)
                        }
                        style={{ width: "44px", height: "34px", padding: "2px" }}
                      />
                      <Form.Control
                        type="text"
                        value={formData.sidebarActiveBg}
                        onChange={(e) =>
                          handleSidebarColorChange("sidebarActiveBg", e.target.value)
                        }
                        placeholder="(usa a cor primária)"
                        style={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Vazio = usa a cor primária da marca.
                    </Form.Text>
                  </Col>
                </Row>

                {contrastRatio(formData.sidebarText, formData.sidebarBg) < 4.5 && (
                  <Alert variant="warning" className="border-0 py-2">
                    <small className="d-flex align-items-start">
                      <IconAlertTriangle size={16} className="me-2 mt-1 flex-shrink-0" />
                      <span>
                        Contraste baixo entre o texto ({formData.sidebarText}) e o
                        fundo ({formData.sidebarBg}) do menu — pode ficar difícil de
                        ler. Recomendado no mínimo 4.5:1 (atual:{" "}
                        {contrastRatio(
                          formData.sidebarText,
                          formData.sidebarBg
                        ).toFixed(2)}
                        :1).
                      </span>
                    </small>
                  </Alert>
                )}

                <div className="mt-2">
                  <small className="text-muted mb-2 d-block">Preview:</small>
                  <div
                    style={{
                      width: "220px",
                      borderRadius: "10px",
                      overflow: "hidden",
                      border: "1px solid #e2e8f0",
                      background: formData.sidebarBg,
                    }}
                  >
                    <div
                      style={{
                        padding: "10px 12px",
                        fontSize: "10px",
                        fontWeight: 700,
                        letterSpacing: "1px",
                        textTransform: "uppercase",
                        color: formData.sidebarIcon,
                      }}
                    >
                      Menu
                    </div>
                    {[
                      { label: "Dashboard", active: true },
                      { label: "Agentes", active: false },
                      { label: "Relatórios", active: false },
                    ].map((it) => (
                      <div
                        key={it.label}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          margin: "4px 8px",
                          padding: "8px 12px",
                          borderRadius: "8px",
                          background: it.active
                            ? formData.sidebarActiveBg || formData.primaryColor
                            : "transparent",
                          color: it.active
                            ? formData.sidebarActiveText
                            : formData.sidebarText,
                          fontSize: "13px",
                          fontWeight: 500,
                        }}
                      >
                        <span
                          style={{
                            width: "14px",
                            height: "14px",
                            borderRadius: "3px",
                            background: it.active
                              ? formData.sidebarActiveText
                              : formData.sidebarIcon,
                            opacity: 0.9,
                          }}
                        />
                        {it.label}
                      </div>
                    ))}
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Seção de Logotipo */}
            <Card className="mb-4 border-0 shadow-sm">
              <Card.Header className="bg-light border-0">
                <h6 className="mb-0 fw-bold d-flex align-items-center">
                  <IconUpload size={18} className="me-2" />
                  Logotipo e Favicon
                </h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Label className="fw-semibold">
                      Logo Principal
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload("logo", e)}
                      className="mb-2"
                    />
                    <Form.Text className="text-muted">
                      PNG/SVG, máx. 2MB, 200x60px
                    </Form.Text>
                    {logoPreview && (
                      <div className="mt-3 p-3 border rounded text-center bg-light">
                        <img
                          src={logoPreview}
                          alt="Preview do Logo"
                          style={{ maxHeight: "60px", maxWidth: "200px" }}
                        />
                      </div>
                    )}
                  </Col>
                  <Col md={4}>
                    <Form.Label className="fw-semibold">Logo Mobile</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload("logoMobile", e)}
                      className="mb-2"
                    />
                    <Form.Text className="text-muted">
                      PNG/SVG, máx. 2MB, 150x50px
                    </Form.Text>
                    {logoMobilePreview && (
                      <div className="mt-3 p-3 border rounded text-center bg-light">
                        <img
                          src={logoMobilePreview}
                          alt="Preview do Logo Mobile"
                          style={{ maxHeight: "50px", maxWidth: "150px" }}
                        />
                      </div>
                    )}
                  </Col>
                  <Col md={4}>
                    <Form.Label className="fw-semibold">Favicon</Form.Label>
                    <Form.Control
                      type="file"
                      accept=".ico,.png"
                      onChange={(e) => handleFileUpload("favicon", e)}
                      className="mb-2"
                    />
                    <Form.Text className="text-muted">
                      ICO/PNG, máx. 100KB, 32x32px
                    </Form.Text>
                    {faviconPreview && (
                      <div className="mt-3 p-3 border rounded text-center bg-light">
                        <img
                          src={faviconPreview}
                          alt="Preview do Favicon"
                          style={{ width: "32px", height: "32px" }}
                        />
                      </div>
                    )}
                  </Col>
                </Row>
                
                {/* Campo para tamanho do logo */}
                <Row className="mt-4">
                  <Col md={6}>
                    <Form.Label className="fw-semibold">
                      Tamanho do Logo (em pixels)
                    </Form.Label>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Control
                        type="number"
                        min="50"
                        max="500"
                        value={formData.logoWidth}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Permitir apenas números
                          if (/^\d*$/.test(value)) {
                            setFormData(prev => ({ ...prev, logoWidth: value }));
                          }
                        }}
                        placeholder="150"
                        style={{ maxWidth: "120px" }}
                      />
                      <span className="text-muted">px</span>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setShowLogoSizePreview(!showLogoSizePreview)}
                        className="ms-2"
                      >
                        <IconCheck size={16} />
                      </Button>
                    </div>
                    <Form.Text className="text-muted">
                      Largura do logo em pixels. Recomendado 210px.
                    </Form.Text>
                    
                    {/* Preview do tamanho do logo */}
                    {showLogoSizePreview && logoPreview && (
                      <div className="mt-3 p-3 border rounded text-center bg-light">
                        <p className="mb-2 fw-semibold">Preview do tamanho:</p>
                        <img
                          src={logoPreview}
                          alt="Preview do tamanho do Logo"
                          style={{ 
                            width: `${formData.logoWidth || 150}px`,
                            maxHeight: "200px",
                            objectFit: "contain"
                          }}
                        />
                        <p className="mt-2 text-muted small">
                          Largura: {formData.logoWidth || 150}px
                        </p>
                      </div>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Seção de SEO/Meta Tags */}
            <Card className="mb-4 border-0 shadow-sm">
              <Card.Header className="bg-light border-0">
                <h6 className="mb-0 fw-bold d-flex align-items-center">
                  <IconCheck size={18} className="me-2" />
                  SEO & Meta Tags
                </h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Título do Site
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.siteTitle}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            siteTitle: e.target.value,
                          }))
                        }
                        placeholder="Minha Plataforma - Agentes de IA"
                        maxLength="60"
                      />
                      <Form.Text className="text-muted">
                        Título que aparecerá na aba do navegador e redes sociais (máx. 60 caracteres)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        Descrição do Site
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={formData.siteDescription}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            siteDescription: e.target.value,
                          }))
                        }
                        placeholder="Descrição que aparecerá nos resultados de busca e redes sociais"
                        maxLength="160"
                      />
                      <Form.Text className="text-muted">
                        Descrição para motores de busca e compartilhamento (máx. 160 caracteres)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Seção de Banner Social */}
            <Card className="mb-4 border-0 shadow-sm">
              <Card.Header className="bg-light border-0">
                <h6 className="mb-0 fw-bold d-flex align-items-center">
                  <IconShare size={18} className="me-2" />
                  Banner para Redes Sociais
                </h6>
              </Card.Header>
              <Card.Body>
                <Alert variant="info" className="border-0 mb-3">
                  <small>
                    Esta imagem sera exibida quando seu site for compartilhado no WhatsApp, Facebook, Twitter e outras redes sociais.
                    Use uma imagem de <strong>1200x630 pixels</strong> para melhor resultado.
                  </small>
                </Alert>
                <Row>
                  <Col md={12}>
                    <Form.Label className="fw-semibold">
                      Banner Social (1200x630px)
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/jpeg,image/png"
                      onChange={(e) => handleFileUpload("socialBanner", e)}
                      className="mb-2"
                    />
                    <Form.Text className="text-muted">
                      JPG/PNG, max. 1MB, dimensao recomendada: 1200x630px
                    </Form.Text>
                    {socialBannerPreview && (
                      <div className="mt-3 p-3 border rounded text-center bg-light">
                        <img
                          src={socialBannerPreview}
                          alt="Preview do Banner Social"
                          style={{ maxWidth: "100%", maxHeight: "200px", objectFit: "contain" }}
                        />
                      </div>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Seção de SMTP White Label */}
            <Card className="mb-4 border-0 shadow-sm">
              <Card.Header className="bg-light border-0">
                <h6 className="mb-0 fw-bold d-flex align-items-center">
                  <IconMail size={18} className="me-2" />
                  Servidor SMTP (E-mail)
                  {smtpServerState.has_smtp_configured && !smtpDisabledNow && (
                    <span className="badge bg-success ms-2">Ativo</span>
                  )}
                  {smtpDisabledNow && (
                    <span className="badge bg-danger ms-2">
                      Desabilitado por falhas
                    </span>
                  )}
                </h6>
              </Card.Header>
              <Card.Body>
                <Alert variant="info" className="border-0 mb-3">
                  <small>
                    Configure seu próprio servidor SMTP para enviar emails com a sua marca.
                    <strong> Enquanto não configurado, os emails são enviados pelo servidor padrão do Mercado Agentes.</strong>
                  </small>
                </Alert>

                {smtpServerState.smtp_last_error && (
                  <Alert variant="warning" className="border-0 mb-3 d-flex align-items-start">
                    <IconAlertTriangle size={20} className="me-2 mt-1 flex-shrink-0" />
                    <div>
                      <strong>Última falha registrada:</strong>
                      <div style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                        {smtpServerState.smtp_last_error}
                      </div>
                      <small className="text-muted">
                        Falhas consecutivas: {smtpServerState.smtp_failure_count}
                      </small>
                    </div>
                  </Alert>
                )}

                <Row>
                  <Col md={8}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Host SMTP</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="smtp.seuprovedor.com"
                        value={smtpData.smtp_host}
                        onChange={(e) => handleSmtpFieldChange("smtp_host", e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Porta</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="587"
                        value={smtpData.smtp_port}
                        onChange={(e) => handleSmtpFieldChange("smtp_port", e.target.value)}
                      />
                      <Form.Text className="text-muted">
                        587 (TLS) ou 465 (SSL)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Usuário</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="usuario@seudominio.com"
                        value={smtpData.smtp_username}
                        onChange={(e) => handleSmtpFieldChange("smtp_username", e.target.value)}
                        autoComplete="off"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold d-flex align-items-center">
                        <IconShieldLock size={14} className="me-1" />
                        Senha
                      </Form.Label>
                      <Form.Control
                        type="password"
                        placeholder={smtpServerState.smtp_password_set ? "(senha salva — preencha para alterar)" : "Senha do SMTP"}
                        value={smtpData.smtp_password}
                        onChange={(e) => handleSmtpFieldChange("smtp_password", e.target.value)}
                        autoComplete="new-password"
                      />
                      <Form.Text className="text-muted">
                        A senha é criptografada antes de ser salva.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Email Remetente</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="noreply@seudominio.com"
                        value={smtpData.smtp_from_email}
                        onChange={(e) => handleSmtpFieldChange("smtp_from_email", e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Nome Remetente</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Minha Marca"
                        value={smtpData.smtp_from_name}
                        onChange={(e) => handleSmtpFieldChange("smtp_from_name", e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="smtp-tls-switch"
                      label="Usar TLS (STARTTLS, porta 587)"
                      checked={!!smtpData.smtp_use_tls}
                      onChange={(e) => {
                        handleSmtpFieldChange("smtp_use_tls", e.target.checked);
                        if (e.target.checked) handleSmtpFieldChange("smtp_use_ssl", false);
                      }}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="smtp-ssl-switch"
                      label="Usar SSL/TLS direto (porta 465)"
                      checked={!!smtpData.smtp_use_ssl}
                      onChange={(e) => {
                        handleSmtpFieldChange("smtp_use_ssl", e.target.checked);
                        if (e.target.checked) handleSmtpFieldChange("smtp_use_tls", false);
                      }}
                    />
                  </Col>
                </Row>

                {smtpDomainMismatch && (
                  <Alert variant="warning" className="border-0 mt-3">
                    <small>
                      ⚠️ O domínio do <strong>Email Remetente</strong> é diferente do domínio do <strong>Usuário</strong>.
                      Alguns provedores SMTP rejeitam isso. Certifique-se de que o domínio remetente
                      tem registros SPF/DKIM configurados.
                    </small>
                  </Alert>
                )}

                <Alert variant="light" className="border mt-3">
                  <small>
                    <strong>Importante:</strong> certifique-se de que o domínio do email remetente
                    tem registros SPF, DKIM e DMARC apontando para o provedor SMTP — caso contrário,
                    seus emails podem ir para spam ou ser rejeitados.
                  </small>
                </Alert>

                <hr className="my-4" />

                {/* === Zona 1: Validação (testar) === */}
                <div className="mb-3">
                  <Form.Label className="fw-semibold mb-2 d-flex align-items-center justify-content-between">
                    <span>
                      <IconPlugConnected size={16} className="me-1 text-muted" />
                      Validar configuração
                    </span>
                    {smtpLastTestStatus === "passed" && (
                      <Badge bg="success" className="d-inline-flex align-items-center">
                        <IconCheck size={12} className="me-1" />
                        Último teste: aprovado
                      </Badge>
                    )}
                    {smtpLastTestStatus === "failed" && (
                      <Badge bg="danger" className="d-inline-flex align-items-center" title={smtpLastTestError || ""}>
                        <IconAlertTriangle size={12} className="me-1" />
                        Último teste: falhou
                      </Badge>
                    )}
                    {smtpLastTestStatus === null && smtpDirty && (
                      <Badge bg="warning" text="dark" className="d-inline-flex align-items-center">
                        Não testado
                      </Badge>
                    )}
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text className="bg-light">
                      <IconMail size={16} />
                    </InputGroup.Text>
                    <Form.Control
                      type="email"
                      placeholder="email@para-receber-teste.com"
                      value={smtpTestRecipient}
                      onChange={(e) => setSmtpTestRecipient(e.target.value)}
                      aria-label="Email para receber o teste"
                    />
                    <Button
                      variant="outline-primary"
                      onClick={handleTestSmtp}
                      disabled={smtpTesting || smtpSaving}
                      style={{ minWidth: 160 }}
                    >
                      {smtpTesting ? (
                        <><Spinner size="sm" className="me-1" /> Testando...</>
                      ) : (
                        <><IconPlugConnected size={16} className="me-1" /> Testar conexão</>
                      )}
                    </Button>
                  </InputGroup>
                  <Form.Text className="text-muted">
                    Um email de teste será enviado para o endereço acima.
                  </Form.Text>
                </div>

                {/* === Zona 2: Persistência (salvar) === */}
                <div className="d-grid mt-4">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleSaveSmtp}
                    disabled={smtpSaving || smtpTesting || !smtpDirty}
                  >
                    {smtpSaving ? (
                      <><Spinner size="sm" className="me-2" /> Salvando configuração...</>
                    ) : (
                      <><IconCheck size={18} className="me-2" /> Salvar configuração SMTP</>
                    )}
                  </Button>
                  <Form.Text className="text-muted text-center mt-2">
                    Ao salvar, executamos um teste de conexão real. Configurações inválidas são rejeitadas automaticamente.
                  </Form.Text>
                </div>

                {/* === Zona 3: Ação destrutiva (remover), separada visualmente === */}
                {smtpServerState.has_smtp_configured && (
                  <div className="border-top mt-4 pt-3 text-end">
                    <Button
                      variant="link"
                      size="sm"
                      onClick={handleClearSmtp}
                      disabled={smtpSaving || smtpTesting}
                      className="text-danger text-decoration-none"
                    >
                      <IconX size={14} className="me-1" />
                      Remover SMTP custom e voltar ao servidor padrão
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Seção de Domínio */}
            <Card className="mb-4 border-0 shadow-sm">
              <Card.Header className="bg-light border-0">
                <h6 className="mb-0 fw-bold d-flex align-items-center">
                  <IconCheck size={18} className="me-2" />
                  Domínio Personalizado
                </h6>
              </Card.Header>
              <Card.Body>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Domínio Customizado (Opcional)
                  </Form.Label>
                  <Form.Control
                    type="url"
                    value={formData.customDomain}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        customDomain: e.target.value,
                      }))
                    }
                    placeholder="https://meudominio.com"
                  />
                  <Form.Text className="text-muted">
                    Configure um domínio próprio para sua organização. Esta
                    funcionalidade requer configuração DNS adicional.
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            {/* Preview Section */}
            <Card className="mb-4 border-0 shadow-sm position-sticky" style={{ top: "20px" }}>
              <Card.Header className="bg-light border-0">
                <h6 className="mb-0 fw-bold d-flex align-items-center">
                  <IconEye size={18} className="me-2" />
                  Preview das Cores
                </h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={12}>
                    <div
                      className="p-3 rounded bg-light"
                      style={{
                        border: `2px solid ${formData.primaryColor}`,
                        minHeight: "120px",
                      }}
                    >
                      <div className="mb-3">
                        <Button
                          size="sm"
                          style={{
                            backgroundColor: formData.primaryColor,
                            borderColor: formData.primaryColor,
                          }}
                        >
                          Botão Primário
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="ms-2"
                          style={{
                            color: formData.secondaryColor,
                            borderColor: formData.secondaryColor,
                          }}
                        >
                          Secundário
                        </Button>
                      </div>
                      <div className="mt-3">
                        <span
                          style={{
                            color: formData.primaryColor,
                            fontWeight: "bold",
                          }}
                        >
                          Texto com cor primária
                        </span>
                        <br />
                        <span
                          style={{
                            color: formData.secondaryColor,
                          }}
                        >
                          Texto com cor secundária
                        </span>
                        <br />
                        <span
                          style={{
                            color: formData.textColor,
                          }}
                        >
                          Texto com cor padrão
                        </span>
                      </div>
                    </div>
                  </Col>
                  <Col md={12} className="mt-3">
                    <Alert
                      variant="info"
                      className="border-0 h-100 d-flex align-items-center"
                    >
                      <div>
                        <strong>Informações:</strong>
                        <ul className="mb-0 mt-2">
                          <li>As alterações serão aplicadas após salvar</li>
                          <li>
                            Pode levar alguns minutos para aparecer em toda
                            plataforma
                          </li>
                          <li>O preview mostra como as cores ficarão</li>
                        </ul>
                      </div>
                    </Alert>
                  </Col>
                </Row>
                
                <div className="d-grid gap-2 mt-3">
                  <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={loading}
                    style={{ 
                      backgroundColor: formData.primaryColor, 
                      borderColor: formData.primaryColor 
                    }}
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Salvando...
                      </>
                    ) : (
                      <>
                        <IconCheck size={16} className="me-1" />
                        Salvar Personalização
                      </>
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default OrganizationCustomization;